import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { SupportTicketCreated, SupportTicketRow } from '@/core/models/workspace.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { workspaceService } from '@/core/services/workspace';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { AddSupportTicket } from './AddSupportTicket';
import { OptionMenu } from './OptionMenu';
import { SearchBox } from './SearchBox';
import { useDebouncedSearch } from './useDebouncedSearch';
import './SupportTicket.scss';

/** prod-p2: shown against tickets whose status is owned by the Capium help desk. */
const capiumStatusHint =
  'This ticket is with Capium support. Its status is set by the help desk and comes back from them.';

const statusOptions = [
  { id: 0, name: 'Open' },
  { id: 1, name: 'Pending' },
  { id: 2, name: 'Resolved' },
  { id: 3, name: 'Closed' },
];
const priorityOptions = [
  { id: 1, name: 'Low' },
  { id: 2, name: 'Normal' },
  { id: 3, name: 'High' },
  { id: 4, name: 'Urgent' },
];

/**
 * prod-p2: changing a status is a permission, so the grid only offers it where the API has said it is allowed. A row
 * that comes back without isCapiumSupport is locked and left to the help desk.
 */
function resolve(row: SupportTicketRow): SupportTicketRow {
  return { ...row, isCapiumSupport: row.isCapiumSupport !== false };
}

/** The firm's support tickets: search, status and priority filters, in-place changes and a New Ticket dialog. */
export function SupportTicket() {
  const [dataSource, setDataSource] = useState<SupportTicketRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // The Angular fields read when a request is sent or answered, mirrored in refs.
  const filters = useRef<{ searchText: string; status: number | null; priority: number | null }>({
    searchText: '',
    status: null,
    priority: null,
  });
  const saving = useRef(false);

  const getList = useCallback(() => {
    const { searchText, status, priority } = filters.current;
    workspaceService
      .getSupportTickets({ searchText: searchText || null, status: status ?? null, priority: priority ?? null })
      .then(
        (response) => setDataSource(response.ticketList.map(resolve)),
        (error) => notifyError(error),
      );
  }, []);

  useEffect(() => {
    getList();
  }, [getList]);

  const onSearch = useDebouncedSearch((text) => {
    filters.current.searchText = text;
    getList();
  });

  const onStatusChange = (value: number | null) => {
    filters.current.status = value;
    setStatusFilter(value);
    getList();
  };

  const onPriorityChange = (value: number | null) => {
    filters.current.priority = value;
    setPriorityFilter(value);
    getList();
  };

  const setSaving = (value: boolean) => {
    saving.current = value;
    setIsSaving(value);
  };

  /** A row that no longer matches the filters drops out of the grid. */
  const replace = (saved: SupportTicketRow) => {
    const row = resolve(saved);
    const { status, priority } = filters.current;
    const matchesStatus = status === null || status === row.statusId;
    const matchesPriority = priority === null || priority === row.priorityId;
    setDataSource((rows) =>
      matchesStatus && matchesPriority
        ? rows.map((r) => (r.id === row.id ? row : r))
        : rows.filter((r) => r.id !== row.id),
    );
  };

  /** Moves a ticket to another status; the row is replaced with what the API returns. */
  const setStatus = (row: SupportTicketRow, status: number) => {
    // prod-p2: the Capium help desk owns the status of tickets raised with it; the API refuses these too.
    if (row.isCapiumSupport) {
      notifyError(capiumStatusHint);
      return;
    }
    if (row.statusId === status || saving.current) {
      return;
    }
    setSaving(true);
    workspaceService
      .setSupportTicketStatus(row.id, status)
      .then(
        (saved) => {
          replace(saved);
          notifySuccess(`${saved.ticket} is now ${saved.status}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setSaving(false));
  };

  const setPriority = (row: SupportTicketRow, priority: number) => {
    if (row.priorityId === priority || saving.current) {
      return;
    }
    setSaving(true);
    workspaceService
      .setSupportTicketPriority(row.id, priority)
      .then(
        (saved) => {
          replace(saved);
          notifySuccess(`${saved.ticket} priority set to ${saved.priority}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setSaving(false));
  };

  const openAddDialog = () => {
    void openDialog<unknown, SupportTicketCreated>(AddSupportTicket)
      .afterClosed()
      .then((result) => {
        if (result?.id) {
          notifySuccess(result.message);
          getList();
        }
      });
  };

  // APT239: as in 3.0 (Tickets/Default.aspx), a ticket opens on its own page with its thread of messages and replies.
  const columns: DataTableColumn<SupportTicketRow>[] = [
    {
      id: 'ticket',
      header: 'Ticket No.',
      headerClassName: 'w-150',
      cell: (row) => (
        <Link className="link" to={`/home/ticket/${row.id}`}>
          {row.ticket}
        </Link>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (row) => (
        <Link className="link" to={`/home/ticket/${row.id}`} title="View the ticket and its replies">
          {row.subject}
        </Link>
      ),
    },
    { id: 'update', header: 'Created on', cell: (row) => row.update },
    {
      id: 'status',
      header: 'Status',
      headerClassName: 'w-150',
      cell: (row) => (
        <div className="d-flex align-items-center gap-10">
          <span className={row.statusId === 0 || row.statusId === 1 ? 'success' : ''}>{row.status}</span>
          {/* prod-p2: a ticket with Capium support is moved by the help desk, so the firm cannot set its status. */}
          {row.isCapiumSupport ? (
            <Tooltip title={capiumStatusHint} placement="right">
              <Icon name="lock_outline" outlined className="font-size-16 text-primary" />
            </Tooltip>
          ) : (
            <OptionMenu
              title="Change status"
              disabled={isSaving}
              options={statusOptions}
              currentId={row.statusId}
              onSelect={(id) => setStatus(row, id)}
            />
          )}
        </div>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      headerClassName: 'w-150',
      cell: (row) => (
        <div className="d-flex align-items-center gap-10">
          <span className={row.priorityId >= 3 ? 'text-danger-100' : ''}>{row.priority}</span>
          <OptionMenu
            title="Change priority"
            disabled={isSaving}
            options={priorityOptions}
            currentId={row.priorityId}
            onSelect={(id) => setPriority(row, id)}
          />
        </div>
      ),
    },
    { id: 'department', header: 'Department', cell: (row) => row.department },
  ];

  return (
    <div className="ticket-wrapper support-ticket-page">
      <div className="sidenav-body-content">
        <div className="content-header">
          <div className="content-header-left">
            <Button className="action-button primary-button" onClick={openAddDialog}>
              <Icon name="add" />
              <span>Add Support Ticket</span>
            </Button>
            <SearchBox onSearch={(text) => onSearch((text ?? '').trim())} />
          </div>
          <div className="content-header-right">
            <div className="sidenav-content d-flex gap-10">
              <Select
                className="w-120"
                placeholder="Priority"
                items={priorityOptions}
                bindLabel="name"
                bindValue="id"
                value={priorityFilter}
                onChange={(value) => onPriorityChange(value as number | null)}
                openOnFocus
                panelClass="myPanelClass"
              />
              <Select
                className="w-120"
                placeholder="Status"
                items={statusOptions}
                bindLabel="name"
                bindValue="id"
                value={statusFilter}
                onChange={(value) => onStatusChange(value as number | null)}
                openOnFocus
                panelClass="myPanelClass"
              />
            </div>
          </div>
        </div>
        <div className="content-body p-0">
          <div className="mat-table-wrapper no-hover">
            <DataTable stickyHeader columns={columns} rows={dataSource} rowKey={(row) => row.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
